/* eslint-disable @typescript-eslint/indent */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, Icon, IconButton, LinearProgress, Pagination, Paper, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Chip, Box, Typography, useTheme, alpha, Stack, Tooltip } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LinhasEsqueletoTabela } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { getRowsPerPageCache, setRowsPerPageCache } from '../../../shared/utils/paginaRowsCache';
import { IListagemNfeEntrada } from '../../../shared/services/api/Financeiro/NfeEntrada';
import dayjs from 'dayjs';
import { ServiceNfeEntrada } from '../../../shared/services/api/Financeiro/NfeEntrada';
import { NfeService } from '../../../shared/services/api/Financeiro/fiscal';
import { CelulaListaMobile, LinhaListagemMobile, ListaMobile, useCelular, moedaMobile } from '../../../shared/components/listagem-mobile';

export const ListagemDeNfeEntrada: React.FC = () => {
  const listaCelular = useCelular();
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const ROWS_CACHE_KEY = 'nfeentrada';
  const [rowsPerPage, setRowsPerPage] = useState(() => getRowsPerPageCache(ROWS_CACHE_KEY) ?? Environment.LIMITE_DE_LINHASSMALL);
  const [availableHeight, setAvailableHeight] = useState<number>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const [rows, setRows] = useState<IListagemNfeEntrada[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
  };

  const busca = useMemo(() => {
    return searchParams.get('busca') || '';
  }, [searchParams]);

  const basicselect = useMemo(() => {
    return searchParams.get('basicselect') || 'n.dhemi';
  }, [searchParams]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);
  
  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      ServiceNfeEntrada.getAll(pagina, busca, rowsPerPage)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTotalCount(result.totalCount);
            setRows(result.data);
          }
        });
    });
  }, [busca, pagina, basicselect, rowsPerPage]);

  useEffect(() => {
    if (pagina !== 1) {
      setSearchParams({ busca, basicselect, pagina: '1' }, { replace: true });
    }
  }, [rowsPerPage]);

  const handlePrint = (valor: string) => {
    setIsLoading(true);
    NfeService.Imprimirnfeentrada(valor)
      .then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          const blob = new Blob([result], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => URL.revokeObjectURL(url), 10000);
        }
      });
  };
  
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Altura de uma linha nesta tabela: os Chips de ação (28px normal / 20px denso) + o padding
  // vertical da célula é quem domina a altura real da linha, não o texto. Usamos o mesmo
  // limiar de 480px do "dense" abaixo para escolher qual altura considerar.
  const DENSE_THRESHOLD = 480;
  const ALTURA_LINHA_NORMAL = 51; // chip 28px + padding 10px*2 + borda
  const ALTURA_LINHA_DENSE = 27; // chip 20px + padding 2px*2 + borda

  const calcularLimite = () => {
    const container = containerRef.current;
    if (!container) return;

    // Mede o espaço real até o rodapé da janela em vez de usar um valor fixo de
    // "calc(100vh - Npx)", que fica desalinhado com a altura real do título/toolbar.
    const BOTTOM_GAP = 16;
    const alturaContainer = Math.max(200, window.innerHeight - container.getBoundingClientRect().top - BOTTOM_GAP);

    const headerEl = container.querySelector('thead');
    const alturaHeader = headerEl ? headerEl.getBoundingClientRect().height : 34;

    // Reservada como constante (não medida do <tfoot>): a paginação só existe no DOM quando
    // totalCount > rowsPerPage, o que criaria uma dependência circular com o próprio cálculo.
    const FOOTER_RESERVADO = 56;
    const MIN_ROWS = 1;
    const MAX_ROWS = 100;
    const FOLGA_LINHAS = 1;
    const alturaLinha = alturaContainer < DENSE_THRESHOLD ? ALTURA_LINHA_DENSE : ALTURA_LINHA_NORMAL;
    const alturaDisponivel = alturaContainer - alturaHeader - FOOTER_RESERVADO;
    const novoLimite = Math.min(
      MAX_ROWS,
      Math.max(MIN_ROWS, Math.floor(alturaDisponivel / alturaLinha) - FOLGA_LINHAS)
    );

    setAvailableHeight(prev => (prev === alturaContainer ? prev : alturaContainer));
    setRowsPerPageCache(ROWS_CACHE_KEY, novoLimite);
    setRowsPerPage(prev => (prev === novoLimite ? prev : novoLimite));
  };

  // useLayoutEffect para rodar antes da pintura e antes do efeito de busca (que depende de
  // rowsPerPage), garantindo que a primeira requisição já saia com o limite correto.
  useLayoutEffect(() => {
    calcularLimite();
  }, []);

  useEffect(() => {
    window.addEventListener('resize', calcularLimite);
    return () => window.removeEventListener('resize', calcularLimite);
  }, []);

  const truncarTexto = (texto: string | undefined | null, max: number) => {
    if (!texto) return '-';
    return texto.length > max ? `${texto.slice(0, max)}...` : texto;
  };

  // Tier de compactação de acordo com a largura real do container (não da janela),
  // já que o menu lateral reduz o espaço disponível para a tabela.
  const tier = (() => {
    if (containerWidth === 0) return 3;
    if (containerWidth < 380) return 0;
    if (containerWidth < 480) return 1;
    if (containerWidth < 600) return 2;
    if (containerWidth < 760) return 3;
    if (containerWidth < 950) return 4;
    if (containerWidth < 1150) return 5;
    return 6;
  })();

  const nomeMaxChars = [5, 7, 10, 14, 18, 24, 40][tier];
  const fantasiaMaxChars = [4, 5, 7, 10, 13, 18, 32][tier];
  const showInscricao = tier >= 3; // >= 600px
  const showCnpj = tier >= 5; // >= 950px
  const compactAcoes = tier <= 3; // < 760px: botões só com ícone

  // Quando a altura visível do container é curta, comprime o padding/fonte das
  // linhas para caber mais registros sem precisar de scroll vertical.
  const dense = availableHeight !== undefined && availableHeight < DENSE_THRESHOLD;
  const cellPaddingY = dense ? '3px' : '10px';
  const cellPaddingYAction = dense ? '2px' : '10px';
  const headerPaddingY = dense ? '4px' : '10px';
  const headerFontSize = dense ? '0.58rem' : '0.75rem';
  const chipActionHeight = dense ? 20 : 28;

  const formatarCpfCnpj = (cpfCnpj: string | null | undefined) => {
    if (!cpfCnpj) return '';
    const apenasNumeros = cpfCnpj.replace(/\D/g, '');
    if (apenasNumeros.length === 11) {
      return apenasNumeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (apenasNumeros.length === 14) {
      return apenasNumeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return cpfCnpj;
  };
  
  return (
    <LayoutBaseDePagina
      titulo="Listagem de NFE Entrada"
    >
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openmsg}
        autoHideDuration={tms === 'error' ? null : 900}
        onClose={handleCloseMsg}
      >
        <Alert
          onClose={handleCloseMsg}
          severity={tms}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>
      
      <TableContainer
        ref={containerRef}
        component={Paper}
        variant="outlined"
        sx={{
          ml: { xs: 1, sm: 2 },
          mt: 1,
          mb: 1,
          borderRadius: 2.5,
          overflow: 'auto',
          maxHeight: availableHeight ? `${availableHeight}px` : 'calc(100vh - 240px)',
          width: 'calc(100% - 16px)',
          boxShadow: theme.shadows[2],
          scrollbarWidth: 'thin',
          scrollbarColor: `${alpha(theme.palette.text.primary, 0.4)} ${alpha(
            theme.palette.background.paper,
            0.6
          )}`,
          '&::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: alpha(
              theme.palette.background.paper,
              0.6
            ),
            borderRadius: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(
              theme.palette.text.primary,
              theme.palette.mode === 'dark' ? 0.4 : 0.3
            ),
            borderRadius: 8,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: alpha(
              theme.palette.text.primary,
              theme.palette.mode === 'dark' ? 0.6 : 0.5
            ),
          },
        }}
      >
        <Table
          size="medium"
          sx={{
            width: '100%',
            tableLayout: 'auto',
          }}
        >
          {!listaCelular && (
          <TableHead
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              '& th': {
                fontSize: headerFontSize,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: theme.palette.text.secondary,
                backgroundColor: alpha(
                  theme.palette.primary.main,
                  0.06
                ),
                borderBottom: `1px solid ${alpha(
                  theme.palette.divider,
                  0.6
                )}`,
                padding: `${headerPaddingY} 8px`,
              },
            }}
          >
            <TableRow>
              <TableCell align="center">Emissão</TableCell>
              <TableCell align="right">Nº NFE</TableCell>
              <TableCell align="left">Nome</TableCell>
              <TableCell align="left">Fantasia</TableCell>
              {/* Colunas ocultas conforme a largura real disponível na tabela */}
              <TableCell align="left" sx={{ display: showInscricao ? 'table-cell' : 'none' }}>Inscrição</TableCell>
              <TableCell align="left" sx={{ display: showCnpj ? 'table-cell' : 'none' }}>CNPJ</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          )}

          {isLoading && rows.length === 0 && (
            <TableBody>
              <LinhasEsqueletoTabela
                colunas={[
                  { align: 'center', width: '55%' },
                  { align: 'right', width: '45%' },
                  { width: '85%' },
                  { width: '70%' },
                  ...(showInscricao ? [{ width: '55%' }] : []),
                  ...(showCnpj ? [{ width: '60%' }] : []),
                  { align: 'right', width: '50%' },
                  { align: 'center', variant: 'rounded', width: 90, height: 24 },
                ]}
              />
            </TableBody>
          )}
          <TableBody>
            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box py={4} display="flex" flexDirection="column" alignItems="center" gap={1}>
                    <Icon sx={{ fontSize: 40, color: theme.palette.text.secondary }}>
                      search_off
                    </Icon>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontWeight={500}
                    >
                      Nenhum registro encontrado
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {listaCelular ? (
              <CelulaListaMobile>
                <ListaMobile
                  linhas={rows}
                  chave={(row) => row.idnfe}
                  renderizar={(row) => (
                    <LinhaListagemMobile
                      avatar={row.nome || '?'}
                      titulo={row.nome}
                      detalhes={[dayjs(row.dhemi).format('DD/MM/YY'), `Nº ${row.numero}`, formatarCpfCnpj(row.cnpj)]}
                      valor={moedaMobile(row.valor)}
                      corValor="success.main"
                      onClick={() => navigate(`/nfeentrada/${row.idnfe}`)}
                      acoes={(
                        <IconButton size="small" color="primary" title="Imprimir" onClick={() => handlePrint(row.idnfe)}>
                          <Icon fontSize="small">print</Icon>
                        </IconButton>
                      )}
                    />
                  )}
                />
              </CelulaListaMobile>
            ) : rows.map(row => (
              <TableRow
                key={row.idnfe}
                hover
                onClick={() => navigate(`/nfeentrada/${row.idnfe}`)}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: theme.palette.background.paper,
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    backgroundColor: alpha(
                      theme.palette.primary.main,
                      0.12
                    ),
                    boxShadow: `0 6px 20px ${alpha(
                      theme.palette.common.black,
                      0.12
                    )}`,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <TableCell align="center" sx={{ fontSize: { xs: '0.62rem', sm: '0.8rem' }, padding: `${cellPaddingY} 8px` }}>
                  {dayjs(row.dhemi).format('DD/MM/YYYY')}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontSize: { xs: '0.62rem', sm: '0.75rem' },
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    padding: `${cellPaddingY} 8px`,
                  }}
                >
                  {row.numero}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: { xs: '0.6rem', sm: '0.85rem' },
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    padding: `${cellPaddingYAction} 6px`,
                  }}
                >
                  <Tooltip title={row.nome} arrow>
                    <span>{truncarTexto(row.nome, nomeMaxChars)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: { xs: '0.58rem', sm: '0.8rem' },
                    color: theme.palette.text.secondary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    padding: `${cellPaddingYAction} 6px`,
                  }}
                >
                  <Tooltip title={row.fantasia || '-'} arrow>
                    <span>{truncarTexto(row.fantasia, fantasiaMaxChars)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    fontFamily: 'monospace',
                    color: theme.palette.text.secondary,
                    display: showInscricao ? 'table-cell' : 'none',
                    padding: `${cellPaddingY} 8px`,
                  }}
                >
                  {row.ie || '-'}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    fontFamily: 'monospace',
                    color: theme.palette.text.secondary,
                    display: showCnpj ? 'table-cell' : 'none',
                    padding: `${cellPaddingY} 8px`,
                  }}
                >
                  {formatarCpfCnpj(row.cnpj)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontSize: { xs: '0.65rem', sm: '0.85rem' },
                    fontWeight: 700,
                    color: theme.palette.success.dark,
                    whiteSpace: 'nowrap',
                    padding: `${cellPaddingY} 8px`,
                  }}
                >
                  {Number(row.valor || 0).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </TableCell>
                <TableCell align="center" sx={{ padding: `${cellPaddingYAction} 6px` }}>
                  <Stack direction="row" spacing={{ xs: 0.25, sm: 0.5 }} justifyContent="center">
                    <Tooltip title="Imprimir NFE" arrow>
                      <Chip
                        icon={<Icon sx={{ fontSize: { xs: 15, sm: 18 } }}>print</Icon>}
                        label={compactAcoes ? undefined : 'Imprimir'}
                        clickable
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePrint(row.idnfe);
                        }}
                        sx={{
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          fontWeight: 600,
                          height: chipActionHeight,
                          bgcolor: alpha(theme.palette.primary.main, 0.15),
                          color: theme.palette.primary.dark,
                          '& .MuiChip-label': {
                            px: compactAcoes ? 0 : { xs: 0.5, sm: 1 },
                            display: compactAcoes ? 'none' : 'block',
                          },
                          '& .MuiChip-icon': {
                            fontSize: { xs: 15, sm: 18 },
                            ml: compactAcoes ? 0 : { xs: '4px', sm: '5px' },
                            mr: compactAcoes ? 0 : undefined,
                          },
                          ...(compactAcoes && {
                            width: 28,
                            '& .MuiChip-icon': {
                              fontSize: 16,
                              margin: 0,
                            },
                          }),
                          '&:hover': {
                            bgcolor: alpha(
                              theme.palette.primary.main,
                              0.25
                            ),
                          },
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Lançar nota fiscal" arrow>
                      <Chip
                        icon={<Icon sx={{ fontSize: { xs: 15, sm: 18 } }}>inventory_2</Icon>}
                        label={compactAcoes ? undefined : 'Lançar'}
                        clickable
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/nfeentrada/${row.idnfe}`);
                        }}
                        sx={{
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          fontWeight: 600,
                          height: chipActionHeight,
                          bgcolor: alpha(theme.palette.success.main, 0.15),
                          color: theme.palette.success.dark,
                          '& .MuiChip-label': {
                            px: compactAcoes ? 0 : { xs: 0.5, sm: 1 },
                            display: compactAcoes ? 'none' : 'block',
                          },
                          '& .MuiChip-icon': {
                            fontSize: { xs: 15, sm: 18 },
                            ml: compactAcoes ? 0 : { xs: '4px', sm: '5px' },
                            mr: compactAcoes ? 0 : undefined,
                          },
                          ...(compactAcoes && {
                            width: 28,
                            '& .MuiChip-icon': {
                              fontSize: 16,
                              margin: 0,
                            },
                          }),
                          '&:hover': {
                            bgcolor: alpha(
                              theme.palette.success.main,
                              0.25
                            ),
                          },
                        }}
                      />
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={8}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {totalCount > rowsPerPage && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Box display="flex" justifyContent="center" py={2}>
                    <Pagination
                      page={pagina}
                      count={Math.ceil(
                        totalCount /
                        rowsPerPage
                      )}
                      onChange={(_, newPage) =>
                        setSearchParams(
                          {
                            busca,
                            basicselect,
                            pagina: newPage.toString(),
                          },
                          { replace: true }
                        )
                      }
                      size="small"
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableFooter>
        </Table>
      </TableContainer>
    </LayoutBaseDePagina>
  );
};